import React, { useState } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import {
  ProductHeader,
  ProductSdsCard,
  BaseLanguageTranslation,
  OtherTranslationsGrid,
  VersionHistoryDialog,
  DeleteConfirmationDialog,
  ProductEmbedDialog
} from '../components/product-details';
import { useProductDetails } from '../hooks/useProductDetails';
import { useTranslation } from 'react-i18next';
import PageBreadcrumbs from '../components/Breadcrumbs';

const ProductDetails: React.FC = () => {
  const { t } = useTranslation();
  const {
    product,
    baseLanguageTranslation,
    otherTranslations,
    error,
    sdsError,
    sdsWarning,
    loading,
    isEditing,
    editForm,
    editFieldErrors,
    deleteDialogOpen,
    deleting,
    versionHistoryOpen,
    selectedLanguage,
    versionHistory,
    historyLoading,
    previewVersion,
    previewLoading,
    compareDiff,
    compareLoading,
    canRollbackTranslation,
    rollbackLoading,
    canManageEmbed,
    canAddSds,
    canReplaceSds,
    canDeleteSds,
    sdsUploading,
    sdsDeleting,
    sdsDownloading,
    embedDialogOpen,
    embed,
    embedLoading,
    embedActionLoading,
    embedError,
    handleEdit,
    handleCancelEdit,
    handleSaveEdit,
    handleFormChange,
    handlePictogramsChange,
    handleNavigateToTranslate,
    handleEditTranslation,
    handleCopyText,
    handleOpenEmbedDialog,
    handleCloseEmbedDialog,
    publishEmbed,
    disableEmbed,
    rotateEmbed,
    handleCopyEmbedCode,
    openVersionHistory,
    openVersionPreview,
    openVersionCompareWithCurrent,
    rollbackTranslationVersion,
    backToVersionHistoryList,
    closeVersionHistory,
    handleDeleteClick,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleSdsUpload,
    handleSdsDelete,
    handleSdsDownload,
  } = useProductDetails();

  const [sdsDrawerOpen, setSdsDrawerOpen] = useState(false);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="body1">{t('nav.loading')}</Typography>
      </Box>
    );
  }

  if (!product) {
    return error ? <Alert severity="error">{error}</Alert> : null;
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <PageBreadcrumbs
        items={[
          { label: t('nav.products'), path: '/products' },
          { label: product.name },
        ]}
      />

      {/* Product Header */}
      <ProductHeader
        product={product}
        translations={[...(baseLanguageTranslation ? [baseLanguageTranslation] : []), ...otherTranslations]}
        isEditing={isEditing}
        editForm={editForm}
        fieldErrors={editFieldErrors}
        canAddSds={canAddSds}
        canReplaceSds={canReplaceSds}
        canDeleteSds={canDeleteSds}
        canManageEmbed={canManageEmbed}
        sdsDownloading={sdsDownloading}
        onEdit={handleEdit}
        onSave={handleSaveEdit}
        onCancel={handleCancelEdit}
        onFormChange={handleFormChange}
        onPictogramsChange={handlePictogramsChange}
        onNavigateToTranslate={handleNavigateToTranslate}
        onDelete={handleDeleteClick}
        onManageEmbed={handleOpenEmbedDialog}
        onManageSds={() => setSdsDrawerOpen(true)}
        onSdsDownload={handleSdsDownload}
      />

      <ProductEmbedDialog
        open={embedDialogOpen}
        productName={product.name}
        embed={embed}
        loading={embedLoading}
        actionLoading={embedActionLoading}
        error={embedError}
        onClose={handleCloseEmbedDialog}
        onPublish={publishEmbed}
        onDisable={disableEmbed}
        onRotate={rotateEmbed}
        onCopyCode={handleCopyEmbedCode}
      />

      <ProductSdsCard
        open={sdsDrawerOpen}
        product={product}
        canAddSds={canAddSds}
        canReplaceSds={canReplaceSds}
        canDeleteSds={canDeleteSds}
        uploadLoading={sdsUploading}
        downloadLoading={sdsDownloading}
        deleteLoading={sdsDeleting}
        error={sdsError}
        warning={sdsWarning}
        onClose={() => setSdsDrawerOpen(false)}
        onUpload={handleSdsUpload}
        onDownload={handleSdsDownload}
        onDelete={handleSdsDelete}
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Base Language Section */}
      {baseLanguageTranslation && (
        <BaseLanguageTranslation
          translation={baseLanguageTranslation}
          product={product}
          onCopyText={handleCopyText}
          onViewHistory={openVersionHistory}
          onEditTranslation={handleEditTranslation}
        />
      )}

      {/* Other Translations Section */}
      <OtherTranslationsGrid
        translations={otherTranslations}
        productId={product.id}
        onCopyText={handleCopyText}
        onViewHistory={openVersionHistory}
        onEditTranslation={handleEditTranslation}
        onNavigateToTranslate={handleNavigateToTranslate}
      />

      {/* Version History Modal */}
      <VersionHistoryDialog
        open={versionHistoryOpen}
        onClose={closeVersionHistory}
        language={selectedLanguage}
        versionHistory={versionHistory}
        loading={historyLoading}
        previewVersion={previewVersion}
        previewLoading={previewLoading}
        compareDiff={compareDiff}
        compareLoading={compareLoading}
        canRollback={canRollbackTranslation}
        rollbackLoading={rollbackLoading}
        onPreviewVersion={openVersionPreview}
        onCompareWithCurrent={openVersionCompareWithCurrent}
        onRollbackVersion={rollbackTranslationVersion}
        onBackToVersions={backToVersionHistoryList}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        productName={product.name}
        translationCount={[...(baseLanguageTranslation ? [baseLanguageTranslation] : []), ...otherTranslations].length}
        loading={deleting}
      />
    </Box>
  );
};

export default ProductDetails;
